import {
  ActionItemSelectedIcon,
  Button,
  Globe01Icon,
  SelectCombobox,
  SelectComboboxItem,
  SelectComboboxList,
  SelectComboboxMatches,
  SelectComboboxPopover,
  SelectComboboxSearchInput,
  SelectComboboxTrigger
} from '@translated/babele-react'
import {SupportedLanguage, applicationLanguages} from '~/utils/locales'
import {useLocation} from 'react-router'
import {constructPathWithLangPair, getLocalizedPath, getPathFromLocalizedPath} from '~/utils/routes'
import {memo} from 'react'
import {useTranslation} from 'react-i18next'
import {useRootLoaderData} from '~/utils/useRootLoaderData'

// This tells the SelectCombobox to show suggestion based
// on wether the option "contains" the user's query
const matchSorterThresholdId = 3

export const LanguageMenu = memo(
  ({dismissLanguageSuggestion}: {dismissLanguageSuggestion: () => void}) => {
    const {pathname} = useLocation()
    const currentPath = getPathFromLocalizedPath(pathname)

    const {pathData} = useRootLoaderData()
    const currentLanguage = pathData.language
    const langPair = pathData.langPair

    const {t} = useTranslation()

    const location = useLocation()

    const onLanguageChange = (lang: string) => {
      if (!currentPath) {
        console.error('No current path found')
        return
      }

      const localizedPath = getLocalizedPath({
        path: currentPath,
        language: lang as SupportedLanguage
      })

      if (!localizedPath) {
        console.error('No localized path found')
        return
      }

      const finalPath = constructPathWithLangPair(localizedPath, langPair)

      if (!finalPath) {
        console.error("Wasn't able to change the app's language")
        return
      }

      window.location.href = finalPath + location.search
    }

    return (
      <SelectCombobox
        variant={{size: 'sm'}}
        value={currentLanguage}
        onValueChange={onLanguageChange}
        onOpenChange={dismissLanguageSuggestion}
        placement="bottom-end"
      >
        <SelectComboboxTrigger
          render={
            <Button
              variant={{mode: 'ghost'}}
              className="!text-neutral-25 transition-all duration-300 hover:!bg-neutral-25/2"
              aria-label={'Change language'}
              // Translations not yet provided
              // aria-label={t('change_language')}
            >
              <Globe01Icon />
            </Button>
          }
        />
        <SelectComboboxPopover
          portal
          gutter={4}
          unmountOnHide
          className="z-20 max-h-[min(var(--popover-available-height,22.5rem),22.5rem)] max-w-[13.5rem]"
        >
          <SelectComboboxSearchInput placeholder={t('search')} />
          <SelectComboboxMatches
            items={applicationLanguages}
            matchSorterOptions={{
              keys: ['label'],
              threshold: matchSorterThresholdId
            }}
          >
            {({matches}) => (
              <SelectComboboxList>
                {matches.map(({code, label}) => (
                  <SelectComboboxItem
                    className="flex items-center justify-between text-neutral-1000"
                    key={code}
                    value={code}
                  >
                    {label}
                    <ActionItemSelectedIcon />
                  </SelectComboboxItem>
                ))}
              </SelectComboboxList>
            )}
          </SelectComboboxMatches>
        </SelectComboboxPopover>
      </SelectCombobox>
    )
  }
)
